import { create } from "zustand";
import { ProjectApi } from "../api/projectApi";
import { useProjectStore } from "../core_stores/useProjectStore";

interface ProjectIconActions {
  initialize: () => Promise<void>;
  setUploadedImage: (uploadedImage: File | null) => void;
  setImageToCrop: (imageToCrop: string | null) => void;
  setIsUploading: (isUploading: boolean) => void;
  setShowCropper: (showCropper: boolean) => void;
  saveUploadedImage: () => Promise<void>;
  handleCropConfirm: (croppedImage: File) => void;
  handleCropCancel: () => void;
  clear: () => void;
}

interface ProjectIconState {
  originalImage: string | null;
  uploadedImage: File | null;
  imageToCrop: string | null;
  isUploading: boolean;
  showCropper: boolean;

  actions: ProjectIconActions;
}

const useProjectIconStore = create<ProjectIconState>()((set) => {
  return {
    originalImage: null,
    uploadedImage: null,
    imageToCrop: null,
    isUploading: false,
    showCropper: false,

    actions: {
      initialize: async () => {
        const projectId = useProjectStore.getState().projectId;
        if (!projectId) {
          return;
        }
        try {
          const icon = await ProjectApi.loadProjectIcon(projectId);
          set({ originalImage: URL.createObjectURL(icon) });
        } catch (error) {
          console.error("Error loading project icon:", error);
          // Don't throw error here as it's not critical for the app to function
        }
      },

      setUploadedImage: (uploadedImage: File | null) => {
        set({ uploadedImage });
      },

      setImageToCrop: (imageToCrop: string | null) => {
        set({ imageToCrop });
      },

      setIsUploading: (isUploading: boolean) => {
        set({ isUploading });
      },

      setShowCropper: (showCropper: boolean) => {
        set({ showCropper });
      },

      handleCropConfirm: (croppedImage: File) => {
        set({
          uploadedImage: croppedImage,
          showCropper: false,
        });
      },

      handleCropCancel: () => {
        set({ showCropper: false, uploadedImage: null });
      },

      saveUploadedImage: async () => {
        const projectId = useProjectStore.getState().projectId;
        const uploadedImage = useProjectIconStore.getState().uploadedImage;

        if (!projectId || !uploadedImage) {
          return;
        }

        try {
          await ProjectApi.uploadIcon({
            projectId,
            icon: uploadedImage,
          });
          const uploadedIcon = await ProjectApi.loadProjectIcon(projectId);
          set({
            originalImage: URL.createObjectURL(uploadedIcon),
            uploadedImage: null,
            imageToCrop: null,
            isUploading: false,
            showCropper: false,
          });
        } catch (error) {
          console.error("Error saving icon:", error);
          throw error;
        }
      },

      clear: () => {
        set({
          originalImage: null,
          uploadedImage: null,
          imageToCrop: null,
          isUploading: false,
          showCropper: false,
        });
      },
    },
  };
});

export const useOriginalIconImage = () =>
  useProjectIconStore((state) => state.originalImage);

export const useUploadedIconImage = () =>
  useProjectIconStore((state) => state.uploadedImage);

export const useIconImageToCrop = () =>
  useProjectIconStore((state) => state.imageToCrop);

export const useShouldShowIconCropper = () =>
  useProjectIconStore((state) => state.showCropper);

export const useIsIconUploading = () =>
  useProjectIconStore((state) => state.isUploading);

export const useProjectIconActions = () =>
  useProjectIconStore((state) => state.actions);
